import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import EmployeeLayout from '../components/EmployeeLayout';
import { createEmployee, getEmployeeProfile, getEmployeeById, updateEmployeeProfile, uploadEmployeeDocument } from '../services/employeeService';
import { apiFetch } from '../utils/apiClient';
import LeaveReportCard from '../components/LeaveReportCard';
import useLocationOptions from '../hooks/useLocationOptions';
import '../styles/Profile.css';
import '../styles/Leave.css';

// Dropdown options for a location field, keeping the saved value selectable even if it isn't in
// the list (e.g. typed free-form before these fields became dropdowns).
const renderLocationOptions = (options, value) => {
  const list = value && !options.includes(value) ? [value, ...options] : options;
  return list.map((name) => (
    <option key={name} value={name}>
      {name}
    </option>
  ));
};

// A Project Manager doesn't report to anyone on their own project, so the
// project-manager field is left blank for them - both on the current project
// and on each row of their project history - show their own name instead of
// a blank field there.
const getProjectManagerDisplay = (employee, managerName) => {
  if (managerName) return managerName;
  if (employee.positionLevel === 'PROJECT_MANAGER') {
    return `${employee.firstName || ''} ${employee.lastName || ''}`.trim();
  }
  return '';
};

function EmployeeProfile({ userId, userRole, onLogout }) {
  const location = useLocation();
  const params = useParams();
  const isCreateMode = userRole === 'admin' && location.pathname.endsWith('/new');
  const profileId = isCreateMode ? null : params.id ? Number(params.id) : userId;
  const isAdminView = userRole === 'admin' && params.id !== undefined && !isCreateMode;
  const [employee, setEmployee] = useState(isCreateMode ? {} : null);
  const [loading, setLoading] = useState(isCreateMode ? false : true);
  const [isEditing, setIsEditing] = useState(isCreateMode);
  const [activeSection, setActiveSection] = useState('personal');
  const [formData, setFormData] = useState({});
  const locationOptions = useLocationOptions(formData.country, formData.state);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem('profile-sidebar-collapsed') === '1';
    } catch {
      return false;
    }
  });
  const navigate = useNavigate();

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('profile-sidebar-collapsed', next ? '1' : '0');
      } catch {
        // localStorage unavailable (private mode, etc.) - collapse still works for this session
      }
      return next;
    });
  };

  const canEdit = true;

  // Define all sections in order
  const sections = [
    { id: 'personal', label: 'Personal Details', icon: '👤' },
    { id: 'address', label: 'Address Details', icon: '📍' },
    { id: 'job', label: 'Job Details', icon: '💼' },
    { id: 'project', label: 'Project Details', icon: '📁' },
    { id: 'salary', label: 'Salary Details', icon: '💰' },
    { id: 'education', label: 'Education Details', icon: '🎓' },
    { id: 'documents', label: 'Document Details', icon: '📄' },
    { id: 'emergency', label: 'Emergency Contact', icon: '🚨' },
   
  ];

  const loadEmployee = useCallback(async () => {
    try {
      setLoading(true);
      const data = isAdminView ? await getEmployeeById(profileId) : await getEmployeeProfile(profileId);
      setEmployee(data);
      setFormData(data);
    } catch (err) {
      setError('Failed to load employee details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [isAdminView, profileId]);

  useEffect(() => {
    if (!isCreateMode) {
      loadEmployee();
    }
  }, [isCreateMode, loadEmployee]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (query.get('edit') === 'true' && canEdit) {
      setIsEditing(true);
    }
  }, [location.search, canEdit]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Changing a parent location invalidates whatever was picked beneath it.
  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'country' && { state: '', city: '' }),
      ...(name === 'state' && { city: '' }),
    }));
  };

  const handleProjectHistoryChange = (index, field, value) => {
    setFormData((prev) => {
      const projectHistory = [...(prev.projectHistory || [])];
      projectHistory[index] = { ...projectHistory[index], [field]: value };
      return { ...prev, projectHistory };
    });
  };

  const handleAddProjectHistoryRow = () => {
    setFormData((prev) => ({
      ...prev,
      projectHistory: [
        ...(prev.projectHistory || []),
        { projectName: '', projectManager: '', startDate: '', endDate: '' },
      ],
    }));
  };

  const handleRemoveProjectHistoryRow = (index) => {
    setFormData((prev) => ({
      ...prev,
      projectHistory: (prev.projectHistory || []).filter((_, i) => i !== index),
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files?.[0];
    if (!file) return;

    if (file.size > 1024 * 1024) {
      alert('File size must be 1 MB or less.');
      e.target.value = null;
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: file,
    }));
  };

  const getFileUrl = (fileValue) => {
    if (!fileValue) return null;
    if (typeof fileValue === 'object') {
      if (fileValue.content) return fileValue.content;
      if (fileValue.url) return fileValue.url;
      if (fileValue.path) return fileValue.path;
    }
    if (typeof fileValue === 'string' && (fileValue.startsWith('data:') || fileValue.startsWith('http') || fileValue.startsWith('blob:'))) {
      return fileValue;
    }
    return null;
  };

  const getDocumentDownloadUrl = (fieldName, fileValue) => {
    const directUrl = getFileUrl(fileValue);
    if (directUrl) return directUrl;
    if (!fileValue) return null;

    const docTypeMap = {
      resumeUpload: 'resume',
      idProofUpload: 'idproof',
      addressProofUpload: 'addressproof',
      educationalCertificates: 'education',
      experienceCertificates: 'experience',
      passportPhoto: 'passport',
      tenthCertificate: 'tenthCertificate',
      intermediateMarksheet: 'intermediateMarksheet',
      provisionalCertificate: 'provisionalCertificate',
      originalDegree: 'originalDegree',
      aadhaarUpload: 'aadhaarUpload',
    };

    const docType = docTypeMap[fieldName] || fieldName;
    const employeeId = profileId || employee?.id || userId;
    if (!employeeId) return null;

    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:8080';
    return `${apiBase}/api/employees/${employeeId}/document/${docType}`;
  };

  const handleDocumentDownload = async (url, fileName) => {
    try {
      const response = await apiFetch(url);
      if (!response.ok) throw new Error('Failed to download document');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || 'document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch (err) {
      alert('Failed to download document');
    }
  };

  const getFileName = (fileValue) => {
    if (!fileValue) return 'document';
    if (typeof fileValue === 'object') {
      return fileValue.name || fileValue.filename || 'document';
    }
    if (fileValue.startsWith('data:')) {
      return 'document';
    }
    const pathParts = fileValue.split('/');
    const fileName = pathParts[pathParts.length - 1] || fileValue;
    return fileName.split('?')[0] || fileName;
  };

  const uploadPendingDocuments = async (employeeId, data) => {
    const documentFields = [
      'resumeUpload',
      'idProofUpload',
      'addressProofUpload',
      'educationalCertificates',
      'experienceCertificates',
      'passportPhoto',
      'tenthCertificate',
      'intermediateMarksheet',
      'provisionalCertificate',
      'originalDegree',
      'aadhaarUpload',
    ];

    for (const fieldName of documentFields) {
      const file = data[fieldName];
      if (file instanceof File) {
        await uploadEmployeeDocument(employeeId, fieldName, file);
      }
    }
  };

  const handleSave = async () => {
    if (isCreateMode && !(formData.employeeId || '').trim()) {
      alert('Employee ID is required');
      setActiveSection('personal');
      return;
    }

    setSaving(true);
    try {
      if (isCreateMode) {
        const result = await createEmployee(formData);
        if (!result.success) {
          throw new Error(result.message);
        }

        if (result.employee?.id) {
          await uploadPendingDocuments(result.employee.id, formData);
          await loadEmployee();
        } else {
          setEmployee(result.employee);
        }

        setIsEditing(false);
        alert('Employee created successfully!');
        navigate('/admin');
      } else {
        const result = await updateEmployeeProfile(profileId, formData);
        if (!result.success) {
          throw new Error(result.message);
        }

        await uploadPendingDocuments(profileId, formData);
        await loadEmployee();

        setIsEditing(false);
        alert('Profile updated successfully!');
      }
    } catch (err) {
      alert(err.message || 'Failed to save profile');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isCreateMode) {
      navigate('/admin');
      return;
    }
    setFormData(employee);
    setIsEditing(false);
  };

  const goToNextSection = () => {
    const currentIndex = sections.findIndex(s => s.id === activeSection);
    if (currentIndex < sections.length - 1) {
      setActiveSection(sections[currentIndex + 1].id);
    }
  };

  const goToPreviousSection = () => {
    const currentIndex = sections.findIndex(s => s.id === activeSection);
    if (currentIndex > 0) {
      setActiveSection(sections[currentIndex - 1].id);
    }
  };

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  if (loading) return <div className="profile-loading">Loading profile...</div>;
  if (error) return <div className="profile-error">{error}</div>;
  if (!employee && !isCreateMode) return <div className="profile-error">No employee data found</div>;

  const currentSectionIndex = sections.findIndex(s => s.id === activeSection);
  const canGoNext = currentSectionIndex < sections.length - 1;
  const canGoPrevious = currentSectionIndex > 0;
  const sectionReadOnly = !isAdminView && !isCreateMode && (activeSection === 'job' || activeSection === 'salary' || activeSection === 'project');
  // Only the client admin can set or change an Employee ID (the backend enforces this too).
  const canEditEmployeeId = userRole === 'admin';

  const renderSectionView = (id) => {
    const F = (label, value, isFile = false, fieldName = '') => {
      // Embedded values (data:/blob:/direct URLs already present on the record) can be linked
      // to directly. Anything else falls back to the protected document API, which requires an
      // Authorization header a plain <a href> can't send, so that case downloads via JS instead.
      const embeddedUrl = getFileUrl(value);
      const apiUrl = isFile && !embeddedUrl ? getDocumentDownloadUrl(fieldName, value) : null;

      if (embeddedUrl) {
        return (
          <div className="info-item">
            <strong>{label}</strong>
            <a href={embeddedUrl} target="_blank" rel="noopener noreferrer" download={getFileName(value)}>
              {getFileName(value)}
            </a>
          </div>
        );
      }

      if (apiUrl) {
        return (
          <div className="info-item">
            <strong>{label}</strong>
            <button type="button" className="doc-link-btn" onClick={() => handleDocumentDownload(apiUrl, getFileName(value))}>
              {getFileName(value)}
            </button>
          </div>
        );
      }

      return <div className="info-item"><strong>{label}</strong> {value || '—'}</div>;
    };

    switch (id) {
      case 'personal':
        return (
          <div className="profile-section">
            <h2>Personal Details</h2>
            <div className="quick-info">
              {F('Employee ID:', employee.employeeId)}
              {F('First Name:', employee.firstName)}
              {F('Last Name:', employee.lastName)}
              {F('Email:', employee.email)}
              {F('Phone:', employee.phone)}
              {F('Alternate Phone:', employee.alternatePhone)}
              {F('Date of Birth:', employee.dateOfBirth)}
              {F('Gender:', employee.gender)}
              {F('Blood Group:', employee.bloodGroup)}
              {F('Marital Status:', employee.maritalStatus)}
              {F('Nationality:', employee.nationality)}
            </div>
          </div>
        );
      case 'address':
        return (
          <div className="profile-section">
            <h2>Address Details</h2>
            <div className="quick-info">
              {F('Permanent Address:', employee.permanentAddress)}
              {F('Current Address:', employee.currentAddress)}
              {F('Country:', employee.country)}
              {F('State:', employee.state)}
              {F('City:', employee.city)}
              {F('Pincode:', employee.pincode)}
            </div>
          </div>
        );
      case 'job':
        return (
          <div className="profile-section">
            <h2>Job Details</h2>
            <div className="quick-info">
              {F('Department:', employee.department)}
              {F('Designation:', employee.designation)}
              {F('Employee Type:', employee.employeeType)}
              {F('Date of Joining:', employee.dateOfJoining)}
              {F('Work Location:', employee.workLocation)}
              {F('Reporting Manager:', employee.reportingManager)}
              {F('Employee Status:', employee.employeeStatus)}
              {F('Shift Timing:', employee.shiftTiming)}
              {F('Years of Experience:', employee.experience)}
              {F('Employee Category:', employee.employeeCategory)}
            </div>
          </div>
        );
      case 'project':
        return (
          <div className="profile-section">
            <h2>Project Details</h2>
            <div className="quick-info">
              {F('Work Status:', employee.workStatus || 'Bench')}
              {employee.workStatus === 'Project' && F('Current Project:', employee.currentProjectName)}
              {employee.workStatus === 'Project' && F('Project Manager:', getProjectManagerDisplay(employee, employee.currentProjectManager))}
              {employee.workStatus === 'Project' && F('Started On:', employee.currentProjectStartDate)}
            </div>

            <h3 style={{ marginTop: '24px' }}>Projects Worked</h3>
            {Array.isArray(employee.projectHistory) && employee.projectHistory.length > 0 ? (
              <div className="table-responsive">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th>Project Name</th>
                      <th>Project Manager</th>
                      <th>Start Date</th>
                      <th>End Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employee.projectHistory.map((entry, index) => (
                      <tr key={entry.id || index}>
                        <td>{entry.projectName || '—'}</td>
                        <td>{getProjectManagerDisplay(employee, entry.projectManager) || '—'}</td>
                        <td>{entry.startDate || '—'}</td>
                        <td>{entry.endDate || 'Ongoing'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No project history recorded.</p>
            )}
          </div>
        );
      case 'salary':
        return (
          <div className="profile-section">
            <h2>Salary Details</h2>
            <div className="quick-info">
              {F('Basic Salary:', employee.basicSalary)}
              {F('Bonus:', employee.bonus)}
              {F('CTC:', employee.ctc)}
              {F('PF Applicable:', employee.pfApplicable)}
              {F('PF Number:', employee.pfNumber)}
              {F('ESI Applicable:', employee.esiApplicable)}
              {F('Bank Name:', employee.bankName)}
              {F('Account Number:', employee.accountNumber)}
              {F('IFSC Code:', employee.ifscCode)}
              {F('Branch Name:', employee.branchName)}
            </div>
          </div>
        );
      case 'education':
        return (
          <div className="profile-section">
            <h2>Education Details</h2>
            <div className="quick-info">
              {F('Highest Qualification:', employee.highestQualification)}
              {F('University/College:', employee.universityCollege)}
              {F('Year of Passing:', employee.yearOfPassing)}
              {F('Percentage/CGPA:', employee.percentageCGPA)}
            </div>
          </div>
        );
      case 'documents':
        return (
          <div className="profile-section">
            <h2>Document Details</h2>
            <div className="quick-info">
              {F('PAN Number:', employee.panNumber)}
              {F('Aadhaar Number:', employee.aadhaarNumber)}
              {F('UAN Number:', employee.uanNumber)}
              {F('Passport Number:', employee.passportNumber)}
              {F('Driving License:', employee.drivingLicense)}
              {F('License Expiry Date:', employee.licenseExpiryDate)}
              {F('Resume:', employee.resumeUpload, true, 'resumeUpload')}
              {F('Passport Photo:', employee.passportPhoto, true, 'passportPhoto')}
              {F('10th Certificate:', employee.tenthCertificate, true, 'tenthCertificate')}
              {F('Intermediate Marksheet:', employee.intermediateMarksheet, true, 'intermediateMarksheet')}
              {F('Provisional Certificate:', employee.provisionalCertificate, true, 'provisionalCertificate')}
              {F('Original Degree:', employee.originalDegree, true, 'originalDegree')}
              {F('Aadhaar Card:', employee.aadhaarUpload, true, 'aadhaarUpload')}
              {F('ID Proof:', employee.idProofUpload, true, 'idProofUpload')}
            </div>
          </div>
        );
      case 'emergency':
        return (
          <div className="profile-section">
            <h2>Emergency Contact Details</h2>
            <div className="quick-info">
              {F('Contact Name:', employee.emergencyContactName)}
              {F('Relationship:', employee.relationship)}
              {F('Phone Number:', employee.emergencyContactNumber)}
              {F('Alternate Number:', employee.emergencyAlternateNumber)}
              {F('Address:', employee.emergencyAddress)}
            </div>
          </div>
        );
      case 'leaves':
        return (
          <div className="profile-section">
            <LeaveReportCard userId={profileId || userId} />
          </div>
        );
      default:
        return null;
    }
  };

  const isEmployeeSelfService = userRole === 'employee' && !isAdminView && !isCreateMode;
  const pageTitle = isCreateMode ? 'Create Employee' : isAdminView ? 'Employee Details' : 'Employee Profile';
  const pageSubtitle = isEmployeeSelfService
    ? 'Review and update your profile details, documents.'
    : null;

  const renderProfileSidebar = (sectionsToShow) => (
    <aside className={`profile-sidebar${sidebarCollapsed ? ' collapsed' : ''}`}>
      <button
        type="button"
        className="profile-sidebar-toggle"
        onClick={toggleSidebarCollapsed}
        title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {sidebarCollapsed ? '»' : '«'}
      </button>
      <div className="sidebar-header">
        {!sidebarCollapsed && <h3>Profile Sections</h3>}
      </div>
      <nav className="sidebar-menu">
        {sectionsToShow.map((section) => (
          <button
            key={section.id}
            className={`menu-item ${activeSection === section.id ? 'active' : ''}`}
            title={section.label}
            onClick={() => setActiveSection(section.id)}
          >
            <span className="menu-icon">{section.icon}</span>
            {!sidebarCollapsed && <span className="menu-label">{section.label}</span>}
          </button>
        ))}
      </nav>
      {!sidebarCollapsed && (
        <div className="sidebar-footer">
          <p className="section-counter">
            {currentSectionIndex + 1} / {sections.length}
          </p>
        </div>
      )}
    </aside>
  );

  const profileBody = !isEditing ? (
        // View Mode - show sidebar and view sections; edit button placed below header on right
        <div className="profile-content">
          {renderProfileSidebar(sections)}

          <main className="profile-main">
            {canEdit && (
              <div className="edit-button-container">
                <button className="edit-btn" onClick={() => {
                  if (activeSection === 'leaves') {
                    setActiveSection('personal');
                  }
                  setIsEditing(true);
                }}>Edit Profile</button>
              </div>
            )}
            {renderSectionView(activeSection)}
          </main>
        </div>
      ) : (
        // Edit Mode - Sidebar with Sections
        <div className="profile-content">
          {/* Sidebar Menu */}
          {renderProfileSidebar(sections.filter((section) => section.id !== 'leaves'))}

          {/* Main Content */}
          <main className="profile-main">
            {/* Personal Details Section */}
            {activeSection === 'personal' && (
              <section className="profile-section">
                <h2>Personal Details</h2>
                <form className="profile-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Employee ID *</label>
                      <input
                        type="text"
                        name="employeeId"
                        value={formData.employeeId || ''}
                        onChange={handleInputChange}
                        placeholder="Enter employee ID"
                        disabled={!canEditEmployeeId}
                        title={canEditEmployeeId ? undefined : 'Only your company admin can change the Employee ID'}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>First Name *</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName || ''}
                        onChange={handleInputChange}
                        placeholder="Enter first name"
                      />
                    </div>
                    <div className="form-group">
                      <label>Last Name *</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName || ''}
                        onChange={handleInputChange}
                        placeholder="Enter last name"
                      />
                    </div>
                  </div>

                  {isCreateMode && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Role *</label>
                        <select
                          name="role"
                          value={formData.role || 'employee'}
                          onChange={handleInputChange}
                        >
                          <option value="employee">Employee</option>
                          <option value="admin">Admin</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Password *</label>
                        <input
                          type="password"
                          name="password"
                          value={formData.password || ''}
                          onChange={handleInputChange}
                          placeholder="Set a password"
                        />
                      </div>
                    </div>
                  )}

                  <div className="form-row">
                    <div className="form-group">
                      <label>Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email || ''}
                        onChange={handleInputChange}
                        placeholder="Enter email"
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone || ''}
                        onChange={handleInputChange}
                        placeholder="Enter phone"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Alternate Phone</label>
                      <input
                        type="tel"
                        name="alternatePhone"
                        value={formData.alternatePhone || ''}
                        onChange={handleInputChange}
                        placeholder="Enter alternate phone"
                      />
                    </div>
                    <div className="form-group">
                      <label>Date of Birth</label>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={formData.dateOfBirth || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Gender</label>
                      <select
                        name="gender"
                        value={formData.gender || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Blood Group</label>
                      <select
                        name="bloodGroup"
                        value={formData.bloodGroup || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Blood Group</option>
                        <option value="A+">A+</option>
                        <option value="A-">A-</option>
                        <option value="B+">B+</option>
                        <option value="B-">B-</option>
                        <option value="O+">O+</option>
                        <option value="O-">O-</option>
                        <option value="AB+">AB+</option>
                        <option value="AB-">AB-</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Marital Status</label>
                      <select
                        name="maritalStatus"
                        value={formData.maritalStatus || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Status</option>
                        <option value="Single">Single</option>
                        <option value="Married">Married</option>
                        <option value="Divorced">Divorced</option>
                        <option value="Widowed">Widowed</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Nationality</label>
                      <input
                        type="text"
                        name="nationality"
                        value={formData.nationality || ''}
                        onChange={handleInputChange}
                        placeholder="Enter nationality"
                      />
                    </div>
                  </div>
                </form>
              </section>
            )}

            {/* Address Details Section */}
            {activeSection === 'address' && (
              <section className="profile-section">
                <h2>Address Details</h2>
                <form className="profile-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Permanent Address</label>
                      <textarea
                        name="permanentAddress"
                        value={formData.permanentAddress || ''}
                        onChange={handleInputChange}
                        placeholder="Enter permanent address"
                        rows="3"
                      ></textarea>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Current Address</label>
                      <textarea
                        name="currentAddress"
                        value={formData.currentAddress || ''}
                        onChange={handleInputChange}
                        placeholder="Enter current address"
                        rows="3"
                      ></textarea>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Country</label>
                      <select name="country" value={formData.country || ''} onChange={handleLocationChange}>
                        <option value="">Select country</option>
                        {renderLocationOptions(locationOptions.countries, formData.country)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>State</label>
                      {locationOptions.stateMode === 'text' ? (
                        <input
                          type="text"
                          name="state"
                          value={formData.state || ''}
                          onChange={handleLocationChange}
                          placeholder="Enter state"
                        />
                      ) : (
                        <select
                          name="state"
                          value={formData.state || ''}
                          onChange={handleLocationChange}
                          disabled={locationOptions.stateMode === 'disabled'}
                        >
                          <option value="">
                            {locationOptions.stateMode === 'disabled' ? 'Select country first' : 'Select state'}
                          </option>
                          {renderLocationOptions(locationOptions.states, formData.state)}
                        </select>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>City</label>
                      {locationOptions.cityMode === 'text' ? (
                        <input
                          type="text"
                          name="city"
                          value={formData.city || ''}
                          onChange={handleLocationChange}
                          placeholder="Enter city"
                        />
                      ) : (
                        <select
                          name="city"
                          value={formData.city || ''}
                          onChange={handleLocationChange}
                          disabled={locationOptions.cityMode === 'disabled'}
                        >
                          <option value="">
                            {locationOptions.cityMode === 'disabled' ? 'Select state first' : 'Select city'}
                          </option>
                          {renderLocationOptions(locationOptions.cities, formData.city)}
                        </select>
                      )}
                    </div>
                    <div className="form-group">
                      <label>Pincode</label>
                      <input
                        type="text"
                        name="pincode"
                        value={formData.pincode || ''}
                        onChange={handleInputChange}
                        placeholder="Enter pincode"
                      />
                    </div>
                  </div>
                </form>
              </section>
            )}

            {/* Job Details Section */}
            {activeSection === 'job' && (
              <section className="profile-section">
                <h2>Job Details</h2>
                {sectionReadOnly && (
                  <p className="section-note">Job details are not editable in this profile editor.</p>
                )}
                <form className="profile-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Department</label>
                      <select
                        name="department"
                        value={formData.department || ''}
                        onChange={handleInputChange}
                        disabled={sectionReadOnly}
                      >
                        <option value="">Select Department</option>
                        <option value="IT">IT</option>
                        <option value="Non IT">Non IT</option>
                        <option value="HR">HR</option>
                        <option value="Admin">Admin</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Designation</label>
                      <input
                        type="text"
                        name="designation"
                        value={formData.designation || ''}
                        onChange={handleInputChange}
                        placeholder="Enter designation"
                        disabled={sectionReadOnly}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Employee Type</label>
                      <select
                        name="employeeType"
                        value={formData.employeeType || ''}
                        onChange={handleInputChange}
                        disabled={sectionReadOnly}
                      >
                        <option value="">Select Type</option>
                        <option value="Full Time">Full Time</option>
                        <option value="Part Time">Part Time</option>
                        <option value="Contract">Contract</option>
                        <option value="Temporary">Temporary</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Date of Joining</label>
                      <input
                        type="date"
                        name="dateOfJoining"
                        value={formData.dateOfJoining || ''}
                        onChange={handleInputChange}
                        disabled={sectionReadOnly}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Work Location</label>
                      <input
                        type="text"
                        name="workLocation"
                        value={formData.workLocation || ''}
                        onChange={handleInputChange}
                        placeholder="Enter work location"
                        disabled={sectionReadOnly}
                      />
                    </div>
                    <div className="form-group">
                      <label>Reporting Manager</label>
                      <input
                        type="text"
                        name="reportingManager"
                        value={formData.reportingManager || ''}
                        onChange={handleInputChange}
                        placeholder="Enter reporting manager"
                        disabled={sectionReadOnly}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Employee Status</label>
                      <select
                        name="employeeStatus"
                        value={formData.employeeStatus || ''}
                        onChange={handleInputChange}
                        disabled={sectionReadOnly}
                      >
                        <option value="">Select Status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                        <option value="On Leave">On Leave</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Shift Timing</label>
                      <select
                        name="shiftTiming"
                        value={formData.shiftTiming || ''}
                        onChange={handleInputChange}
                        disabled={sectionReadOnly}
                      >
                        <option value="">Select Shift</option>
                        <option value="Morning">Morning</option>
                        <option value="Evening">Evening</option>
                        <option value="Night">Night</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Years of Experience</label>
                      <input
                        type="number"
                        name="experience"
                        value={formData.experience || ''}
                        onChange={handleInputChange}
                        placeholder="Enter years"
                        disabled={sectionReadOnly}
                      />
                    </div>
                    <div className="form-group">
                      <label>Employee Category</label>
                      <input
                        type="text"
                        name="employeeCategory"
                        value={formData.employeeCategory || ''}
                        onChange={handleInputChange}
                        placeholder="Enter category"
                        disabled={sectionReadOnly}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Position Level</label>
                      <select
                        name="positionLevel"
                        value={formData.positionLevel || 'EMPLOYEE'}
                        onChange={handleInputChange}
                        disabled={sectionReadOnly}
                      >
                        <option value="EMPLOYEE">Employee</option>
                        <option value="TEAM_LEAD">Team Lead</option>
                        <option value="PROJECT_MANAGER">Project Manager</option>
                      </select>
                      <p className="section-note">
                        Controls who can be assigned as a Project Manager or Team Lead under Team Structure.
                      </p>
                    </div>
                  </div>
                </form>
              </section>
            )}

            {/* Project Details Section */}
            {activeSection === 'project' && (
              <section className="profile-section">
                <h2>Project Details</h2>
                {sectionReadOnly && (
                  <p className="section-note">Project details are not editable in this profile editor.</p>
                )}
                <form className="profile-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Work Status</label>
                      <select
                        name="workStatus"
                        value={formData.workStatus || 'Bench'}
                        onChange={handleInputChange}
                        disabled={sectionReadOnly}
                      >
                        <option value="Bench">Bench</option>
                        <option value="Project">Project</option>
                      </select>
                    </div>
                  </div>

                  {formData.workStatus === 'Project' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Current Project Name</label>
                        <input
                          type="text"
                          name="currentProjectName"
                          value={formData.currentProjectName || ''}
                          onChange={handleInputChange}
                          placeholder="Enter project name"
                          disabled={sectionReadOnly}
                        />
                      </div>
                      <div className="form-group">
                        <label>Project Manager</label>
                        <input
                          type="text"
                          name="currentProjectManager"
                          value={formData.currentProjectManager || ''}
                          onChange={handleInputChange}
                          placeholder="Enter project manager"
                          disabled={sectionReadOnly}
                        />
                      </div>
                    </div>
                  )}

                  {formData.workStatus === 'Project' && (
                    <div className="form-row">
                      <div className="form-group">
                        <label>Started On</label>
                        <input
                          type="date"
                          name="currentProjectStartDate"
                          value={formData.currentProjectStartDate || ''}
                          onChange={handleInputChange}
                          disabled={sectionReadOnly}
                        />
                      </div>
                    </div>
                  )}
                </form>

                <h3 style={{ marginTop: '24px' }}>Projects Worked</h3>
                <div className="table-responsive">
                  <table className="report-table">
                    <thead>
                      <tr>
                        <th>Project Name</th>
                        <th>Project Manager</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        {!sectionReadOnly && <th>Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {(formData.projectHistory || []).length === 0 ? (
                        <tr>
                          <td colSpan={sectionReadOnly ? 4 : 5} style={{ textAlign: 'center' }}>
                            No project history recorded.
                          </td>
                        </tr>
                      ) : (
                        (formData.projectHistory || []).map((entry, index) => (
                          <tr key={index}>
                            <td>
                              <input
                                type="text"
                                value={entry.projectName || ''}
                                onChange={(e) => handleProjectHistoryChange(index, 'projectName', e.target.value)}
                                placeholder="Project name"
                                disabled={sectionReadOnly}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={entry.projectManager || ''}
                                onChange={(e) => handleProjectHistoryChange(index, 'projectManager', e.target.value)}
                                placeholder="Project manager"
                                disabled={sectionReadOnly}
                              />
                            </td>
                            <td>
                              <input
                                type="date"
                                value={entry.startDate || ''}
                                onChange={(e) => handleProjectHistoryChange(index, 'startDate', e.target.value)}
                                disabled={sectionReadOnly}
                              />
                            </td>
                            <td>
                              <input
                                type="date"
                                value={entry.endDate || ''}
                                onChange={(e) => handleProjectHistoryChange(index, 'endDate', e.target.value)}
                                disabled={sectionReadOnly}
                              />
                            </td>
                            {!sectionReadOnly && (
                              <td>
                                <button
                                  type="button"
                                  className="small-button reject"
                                  onClick={() => handleRemoveProjectHistoryRow(index)}
                                >
                                  Remove
                                </button>
                              </td>
                            )}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {!sectionReadOnly && (
                  <button type="button" className="create-btn" style={{ marginTop: '12px' }} onClick={handleAddProjectHistoryRow}>
                    + Add Project
                  </button>
                )}
              </section>
            )}

            {/* Salary Details Section */}
            {activeSection === 'salary' && (
              <section className="profile-section">
                <h2>Salary Details</h2>
                {sectionReadOnly && (
                  <p className="section-note">Salary details are not editable in this profile editor.</p>
                )}
                <form className="profile-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Basic Salary</label>
                      <input
                        type="number"
                        name="basicSalary"
                        value={formData.basicSalary || ''}
                        onChange={handleInputChange}
                        placeholder="Enter basic salary"
                        disabled={sectionReadOnly}
                      />
                    </div>
                    <div className="form-group">
                      <label>Bonus</label>
                      <input
                        type="number"
                        name="bonus"
                        value={formData.bonus || ''}
                        onChange={handleInputChange}
                        placeholder="Enter bonus"
                        disabled={sectionReadOnly}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>CTC (Cost to Company)</label>
                      <input
                        type="number"
                        name="ctc"
                        value={formData.ctc || ''}
                        onChange={handleInputChange}
                        placeholder="Enter CTC"
                        disabled={sectionReadOnly}
                      />
                    </div>
                    <div className="form-group">
                      <label>PF Applicable</label>
                      <select
                        name="pfApplicable"
                        value={formData.pfApplicable || ''}
                        onChange={handleInputChange}
                        disabled={sectionReadOnly}
                      >
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>PF Number</label>
                      <input
                        type="text"
                        name="pfNumber"
                        value={formData.pfNumber || ''}
                        onChange={handleInputChange}
                        placeholder="Enter PF number"
                        disabled={sectionReadOnly}
                      />
                    </div>
                    <div className="form-group">
                      <label>ESI Applicable</label>
                      <select
                        name="esiApplicable"
                        value={formData.esiApplicable || ''}
                        onChange={handleInputChange}
                        disabled={sectionReadOnly}
                      >
                        <option value="">Select</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Bank Name</label>
                      <input
                        type="text"
                        name="bankName"
                        value={formData.bankName || ''}
                        onChange={handleInputChange}
                        placeholder="Enter bank name"
                        disabled={sectionReadOnly}
                      />
                    </div>
                    <div className="form-group">
                      <label>Account Number</label>
                      <input
                        type="text"
                        name="accountNumber"
                        value={formData.accountNumber || ''}
                        onChange={handleInputChange}
                        placeholder="Enter account number"
                        disabled={sectionReadOnly}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>IFSC Code</label>
                      <input
                        type="text"
                        name="ifscCode"
                        value={formData.ifscCode || ''}
                        onChange={handleInputChange}
                        placeholder="Enter IFSC code"
                        disabled={sectionReadOnly}
                      />
                    </div>
                    <div className="form-group">
                      <label>Branch Name</label>
                      <input
                        type="text"
                        name="branchName"
                        value={formData.branchName || ''}
                        onChange={handleInputChange}
                        placeholder="Enter branch name"
                        disabled={sectionReadOnly}
                      />
                    </div>
                  </div>
                </form>
              </section>
            )}

            {/* Education Details Section */}
            {activeSection === 'education' && (
              <section className="profile-section">
                <h2>Education Details</h2>
                <form className="profile-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Highest Qualification</label>
                      <input
                        type="text"
                        name="highestQualification"
                        value={formData.highestQualification || ''}
                        onChange={handleInputChange}
                        placeholder="E.g., Bachelor's, Master's"
                      />
                    </div>
                    <div className="form-group">
                      <label>University/College</label>
                      <input
                        type="text"
                        name="universityCollege"
                        value={formData.universityCollege || ''}
                        onChange={handleInputChange}
                        placeholder="Enter university/college name"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Year of Passing</label>
                      <input
                        type="number"
                        name="yearOfPassing"
                        value={formData.yearOfPassing || ''}
                        onChange={handleInputChange}
                        placeholder="YYYY"
                      />
                    </div>
                    <div className="form-group">
                      <label>Percentage/CGPA</label>
                      <input
                        type="text"
                        name="percentageCGPA"
                        value={formData.percentageCGPA || ''}
                        onChange={handleInputChange}
                        placeholder="Enter percentage or CGPA"
                      />
                    </div>
                  </div>
                </form>
              </section>
            )}

            {/* Document Details Section */}
            {activeSection === 'documents' && (
              <section className="profile-section">
                <h2>Document Details</h2>
                <form className="profile-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>PAN Number</label>
                      <input
                        type="text"
                        name="panNumber"
                        value={formData.panNumber || ''}
                        onChange={handleInputChange}
                        placeholder="Enter PAN number"
                      />
                    </div>
                    <div className="form-group">
                      <label>Aadhaar Number</label>
                      <input
                        type="text"
                        name="aadhaarNumber"
                        value={formData.aadhaarNumber || ''}
                        onChange={handleInputChange}
                        placeholder="Enter Aadhaar number"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>UAN Number</label>
                      <input
                        type="text"
                        name="uanNumber"
                        value={formData.uanNumber || ''}
                        onChange={handleInputChange}
                        placeholder="Enter UAN number"
                      />
                    </div>
                    <div className="form-group">
                      <label>Passport Number</label>
                      <input
                        type="text"
                        name="passportNumber"
                        value={formData.passportNumber || ''}
                        onChange={handleInputChange}
                        placeholder="Enter passport number"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Driving License</label>
                      <input
                        type="text"
                        name="drivingLicense"
                        value={formData.drivingLicense || ''}
                        onChange={handleInputChange}
                        placeholder="Enter driving license number"
                      />
                    </div>
                    <div className="form-group">
                      <label>License Expiry Date</label>
                      <input
                        type="date"
                        name="licenseExpiryDate"
                        value={formData.licenseExpiryDate || ''}
                        onChange={handleInputChange}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Resume</label>
                      <input
                        type="file"
                        name="resumeUpload"
                        onChange={handleFileChange}
                        accept=".pdf,.doc,.docx"
                      />
                      {formData.resumeUpload && <small>Selected: {formData.resumeUpload.name || formData.resumeUpload}</small>}
                    </div>
                    <div className="form-group">
                      <label>Passport Photo</label>
                      <input
                        type="file"
                        name="passportPhoto"
                        onChange={handleFileChange}
                        accept="image/*"
                      />
                      {formData.passportPhoto && <small>Selected: {formData.passportPhoto.name || formData.passportPhoto}</small>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>10th Certificate</label>
                      <input
                        type="file"
                        name="tenthCertificate"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                      {formData.tenthCertificate && <small>Selected: {formData.tenthCertificate.name || formData.tenthCertificate}</small>}
                    </div>
                    <div className="form-group">
                      <label>Intermediate Marksheet</label>
                      <input
                        type="file"
                        name="intermediateMarksheet"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                      {formData.intermediateMarksheet && <small>Selected: {formData.intermediateMarksheet.name || formData.intermediateMarksheet}</small>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Provisional Certificate</label>
                      <input
                        type="file"
                        name="provisionalCertificate"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                      {formData.provisionalCertificate && <small>Selected: {formData.provisionalCertificate.name || formData.provisionalCertificate}</small>}
                    </div>
                    <div className="form-group">
                      <label>Original Degree</label>
                      <input
                        type="file"
                        name="originalDegree"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                      {formData.originalDegree && <small>Selected: {formData.originalDegree.name || formData.originalDegree}</small>}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Aadhaar Card</label>
                      <input
                        type="file"
                        name="aadhaarUpload"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                      {formData.aadhaarUpload && <small>Selected: {formData.aadhaarUpload.name || formData.aadhaarUpload}</small>}
                    </div>
                    <div className="form-group">
                      <label>Verification ID Proof</label>
                      <input
                        type="file"
                        name="idProofUpload"
                        onChange={handleFileChange}
                        accept=".pdf,.jpg,.jpeg,.png"
                      />
                      {formData.idProofUpload && <small>Selected: {formData.idProofUpload.name || formData.idProofUpload}</small>}
                    </div>
                  </div>
                </form>
              </section>
            )}

            {/* Emergency Contact Section */}
            {activeSection === 'emergency' && (
              <section className="profile-section">
                <h2>Emergency Contact Details</h2>
                <form className="profile-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Contact Name</label>
                      <input
                        type="text"
                        name="emergencyContactName"
                        value={formData.emergencyContactName || ''}
                        onChange={handleInputChange}
                        placeholder="Enter contact name"
                      />
                    </div>
                    <div className="form-group">
                      <label>Relationship</label>
                      <select
                        name="relationship"
                        value={formData.relationship || ''}
                        onChange={handleInputChange}
                      >
                        <option value="">Select Relationship</option>
                        <option value="Spouse">Spouse</option>
                        <option value="Parent">Parent</option>
                        <option value="Sibling">Sibling</option>
                        <option value="Child">Child</option>
                        <option value="Friend">Friend</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Phone Number</label>
                      <input
                        type="tel"
                        name="emergencyContactNumber"
                        value={formData.emergencyContactNumber || ''}
                        onChange={handleInputChange}
                        placeholder="Enter phone number"
                      />
                    </div>
                    <div className="form-group">
                      <label>Alternate Number</label>
                      <input
                        type="tel"
                        name="emergencyAlternateNumber"
                        value={formData.emergencyAlternateNumber || ''}
                        onChange={handleInputChange}
                        placeholder="Enter alternate number"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Address</label>
                      <textarea
                        name="emergencyAddress"
                        value={formData.emergencyAddress || ''}
                        onChange={handleInputChange}
                        placeholder="Enter address"
                        rows="3"
                      ></textarea>
                    </div>
                  </div>
                </form>
              </section>
            )}

            {/* Navigation Buttons */}
            <div className="section-navigation">
              <button
                className="nav-btn prev-btn"
                onClick={goToPreviousSection}
                disabled={!canGoPrevious}
              >
                ← Previous
              </button>
              <span className="section-info">
                {sections[currentSectionIndex].label} ({currentSectionIndex + 1}/{sections.length})
              </span>
              <button
                className="nav-btn next-btn"
                onClick={goToNextSection}
                disabled={!canGoNext}
              >
                Next →
              </button>
            </div>

            <div className="edit-actions">
              <button type="button" className="cancel-btn" onClick={handleCancel} disabled={saving}>
                Cancel
              </button>
              <button type="button" className="save-btn" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Profile'}
              </button>
            </div>
          </main>
        </div>
      );

  if (isEmployeeSelfService) {
    return (
      <EmployeeLayout
        userName={employee?.firstName || ''}
        onLogout={onLogout}
        activeItem="profile"
        title={pageTitle}
        subtitle={pageSubtitle}
      >
        <div className="employee-profile-shell">
          {profileBody}
        </div>
      </EmployeeLayout>
    );
  }

  return (
    <div className="profile-container">
      <header className="profile-header">
        <h1>{pageTitle}</h1>
        <div className="header-actions">
          <span className="employee-name">{employee.firstName} {employee.lastName}</span>
          {(isAdminView || isCreateMode) && (
            <button className="cancel-btn" onClick={() => navigate('/admin')}>
              Back to Dashboard
            </button>
          )}
          <button className="logout-btn" onClick={handleLogout}>Logout</button>
        </div>
      </header>
      {profileBody}
    </div>
  );
}

export default EmployeeProfile;
